import React, { useEffect, useRef } from 'react';
import { useParams, Navigate, Link } from 'react-router-dom';
import { MapPin, Star, Mail, Phone, CheckCircle, ArrowRight } from 'lucide-react';
import { CITIES, PRODUCTS } from '../constants';
import { Button } from '../components/ui/Button';
import { GlassCard } from '../components/ui/GlassCard';

export const Location = ({ onOpenQuote }: { onOpenQuote: () => void }) => {
  const { city } = useParams<{ city: string }>();
  const cityData = city ? CITIES[city.toLowerCase()] : null;
  const parallaxRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (cityData) {
      document.title = cityData.seoTitle;
      const metaDesc = document.querySelector('meta[name="description"]');
      if (metaDesc) {
        metaDesc.setAttribute('content', cityData.seoDescription);
      }
      window.scrollTo(0, 0);
    }
  }, [cityData]);

  useEffect(() => {
    const handleScroll = () => {
      if (parallaxRef.current) {
        const scrolled = window.scrollY;
        parallaxRef.current.style.transform = `translateY(${scrolled * 0.3}px)`;
      }
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  if (!cityData) {
    return <Navigate to="/" replace />;
  }

  return (
    <div className="min-h-screen pt-20">
      
      {/* Location Hero */}
      <section className="relative py-24 px-4 overflow-hidden">
        <div className="absolute inset-0 bg-slate-950 z-0 overflow-hidden">
           {/* Parallax Wrapper for Blobs */}
           <div ref={parallaxRef} className="absolute inset-0 w-full h-full will-change-transform">
             <div className="absolute top-0 right-0 w-[500px] h-[500px] bg-brand-neonBlue/10 rounded-full blur-[100px]"></div>
             <div className="absolute bottom-0 left-0 w-[500px] h-[500px] bg-brand-neonPink/10 rounded-full blur-[100px]"></div>
           </div>
        </div>
        
        <div className="relative z-10 max-w-7xl mx-auto text-center">
          <div className="flex items-center justify-center gap-2 mb-4 text-brand-neonBlue animate-fade-in-up">
            <MapPin size={20} />
            <span className="text-sm font-bold uppercase tracking-widest">{cityData.name}, New Zealand</span>
          </div>
          <h1 className="text-4xl md:text-6xl font-display font-bold text-white mb-6 max-w-4xl mx-auto animate-fade-in-up" style={{ animationDelay: '0.1s' }}>
            {cityData.name} Photo Booth Hire & <br />
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-brand-neonBlue to-brand-neonPink">
              Corporate Activations
            </span>
          </h1>
          <p className="text-lg text-slate-300 max-w-2xl mx-auto mb-10 animate-fade-in-up" style={{ animationDelay: '0.2s' }}>
            {cityData.seoDescription}
          </p>
          <div className="animate-fade-in-up" style={{ animationDelay: '0.3s' }}>
            <Button size="lg" onClick={onOpenQuote}>Check Availability in {cityData.name}</Button>
          </div>
        </div>
      </section>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 grid grid-cols-1 lg:grid-cols-3 gap-12">
        
        {/* Main Content */}
        <div className="lg:col-span-2 space-y-12">
          
          <section>
            <h2 className="text-3xl font-display font-bold text-white mb-6">Why Choose QuikPix {cityData.name}?</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {[
                "Local expert team who knows the venues",
                "Premium lighting designed for dark venues",
                "Instant sharing via 5G connectivity",
                "Custom branded overlays included"
              ].map((feature, i) => (
                <div key={i} className="flex items-start gap-3">
                  <CheckCircle className="text-brand-neonBlue shrink-0" size={24} />
                  <p className="text-slate-300">{feature}</p>
                </div>
              ))}
            </div>
          </section>

          {/* Map Section */}
          <section>
            <h3 className="text-2xl font-bold text-white mb-4">Service Area</h3>
             <GlassCard className="p-0 overflow-hidden h-80 relative flex items-center justify-center bg-slate-900">
                {/* Dynamic Map Placeholder */}
                <div className="absolute inset-0 bg-slate-800 animate-pulse"></div>
                <div className="relative z-10 text-center">
                  <MapPin size={48} className="text-brand-neonPink mx-auto mb-2" />
                  <p className="text-white font-bold">Google Maps Embed</p>
                  <p className="text-sm text-slate-400">Centered on {cityData.coordinates.lat}, {cityData.coordinates.lng}</p>
                </div>
             </GlassCard>
             <p className="text-sm text-slate-500 mt-2">Serving {cityData.name} CBD, and surrounding suburbs within 50km.</p>
          </section>

        </div>

        {/* Sidebar: Licensee Profile */}
        <div className="lg:col-span-1">
          <div className="sticky top-24">
            <GlassCard className="border-brand-neonBlue/20">
              <div className="flex items-center gap-4 mb-6">
                <div className="w-16 h-16 rounded-full overflow-hidden border-2 border-brand-neonBlue">
                  <img src={cityData.operatorImage} alt={cityData.operatorName} className="w-full h-full object-cover" />
                </div>
                <div>
                  <p className="text-xs text-slate-400 uppercase tracking-wide">Local Operator</p>
                  <h3 className="text-xl font-bold text-white">{cityData.operatorName}</h3>
                </div>
              </div>
              
              <div className="space-y-4 mb-6">
                <p className="text-slate-300 text-sm leading-relaxed italic">
                  "{cityData.operatorBio}"
                </p>
                <div className="flex items-center gap-1 text-yellow-400">
                  <Star size={16} fill="currentColor" />
                  <Star size={16} fill="currentColor" />
                  <Star size={16} fill="currentColor" />
                  <Star size={16} fill="currentColor" />
                  <Star size={16} fill="currentColor" />
                  <span className="text-slate-400 text-xs ml-2">(48 Reviews)</span>
                </div>
              </div>

              <div className="space-y-3 pt-6 border-t border-white/10">
                <Button variant="secondary" className="w-full" onClick={onOpenQuote}>
                  Contact {cityData.operatorName.split(' ')[0]}
                </Button>
                <div className="flex justify-center gap-6 mt-4">
                  <a href="#" className="text-slate-400 hover:text-white"><Mail size={20} /></a>
                  <a href="#" className="text-slate-400 hover:text-white"><Phone size={20} /></a>
                </div>
              </div>
            </GlassCard>
          </div>
        </div>

      </div>

      {/* NEW SERVICE GRID SECTION */}
      <section className="py-20 bg-slate-900/50 border-t border-white/10 relative">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
           <div className="text-center mb-16">
             <span className="text-brand-neonBlue font-bold tracking-widest text-xs uppercase mb-2 block">Our Services</span>
             <h2 className="text-3xl md:text-5xl font-display font-bold text-white mb-4">
               Available Experiences in {cityData.name}
             </h2>
             <p className="text-slate-400 max-w-2xl mx-auto">
               From weddings in {cityData.name} CBD to corporate galas. Explore our full range of photo experiences available for hire locally.
             </p>
           </div>

           <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
             {PRODUCTS.map((product) => (
               <Link to={`/product/${product.id}`} key={product.id} className="group block h-full">
                 <GlassCard hoverEffect className="h-full p-0 overflow-hidden flex flex-col">
                   <div className="relative h-48 overflow-hidden">
                     <div className="absolute top-3 right-3 z-10 bg-black/60 backdrop-blur-md text-white text-xs font-bold px-3 py-1 rounded-full border border-white/10">
                       Available in {cityData.name}
                     </div>
                     <img 
                       src={product.image} 
                       alt={`${product.name} hire ${cityData.name}`} 
                       className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-700" 
                     />
                     <div className="absolute inset-0 bg-gradient-to-t from-slate-900 via-transparent to-transparent opacity-60"></div>
                   </div>
                   
                   <div className="p-6 flex-1 flex flex-col">
                     <h3 className="text-xl font-bold text-white mb-2 group-hover:text-brand-neonBlue transition-colors">
                       {product.name}
                     </h3>
                     <p className="text-sm text-slate-400 mb-6 flex-1">
                       {product.description}
                     </p>
                     
                     <div className="flex items-center text-brand-neonBlue text-sm font-bold uppercase tracking-wide gap-2 group-hover:gap-4 transition-all">
                       View Details <ArrowRight size={16} />
                     </div>
                   </div>
                 </GlassCard>
               </Link>
             ))}
           </div>
           
           <div className="mt-16 text-center">
             <div className="inline-block p-[1px] rounded-lg bg-gradient-to-r from-brand-neonBlue to-brand-neonPink">
               <Button className="bg-slate-950 hover:bg-slate-900 text-white rounded-lg border-0" onClick={onOpenQuote}>
                 Get a Quote for {cityData.name}
               </Button>
             </div>
           </div>
        </div>
      </section>

    </div>
  );
};
